<template>
  <v-card :title="t('apply_to_all_cs')">
    <v-card-text>
      <v-row>
        <v-col class="pt-0" style="display: grid">
          <v-row>
            <v-col>
              <p class="text-h6 mb-1">{{ t('money_destination') }}</p>
              <v-autocomplete
                class="mb-2"
                :label="t('money_destination')"
                density="compact"
                clearable
                persistent-clear
                hide-details
                v-model="moneyDestId"
                :items="moneyDests"
                item-title="visual_name"
                item-value="id"
              />
            </v-col>
          </v-row>
          <v-row no-gutters align="end">
            <v-col>
              <v-btn @click="submitMD" class="mt-3" width="100%">{{ t('apply_money_destination_to_all_cs') }}</v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col class="border-s-md pt-0">
          <v-row no-gutters>
            <v-col>
              <p class="text-h6 mb-1">{{ t('charge_re_logo') }}</p>
              <v-autocomplete
                class="mb-2"
                :label="t('frontend_logo_image')"
                density="compact"
                clearable
                persistent-clear
                hide-details
                v-model="logoId"
                :items="parkLogos"
                item-title="visual_name"
                item-value="id"
              />
              <v-divider thickness="2"/>
              <template v-if="logoId !== null">
                <p>{{ t('preview') }}</p>
                <v-img
                  class="border-md"
                  :width="300"
                  :src="logoUrl"
                />
              </template>
              <p v-else>{{ t('choose_logo_preview') }}</p>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <v-btn @click="submitLogo" class="mt-3" width="100%">{{ t('apply_logo_to_all_cs') }}</v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, toRefs, onMounted, watch, computed } from "vue";
import { modifyPatchCS } from "../../remote/park.js";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });
// Special State
const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const props = defineProps(['originalPark', 'parkLogos', 'moneyDests']);
const { originalPark, parkLogos, moneyDests } = toRefs(props);
const emit = defineEmits(['updated']);

// State
const logoId = ref(null);
const moneyDestId = ref(null);

const logoUrl = computed(() => {
  return parkLogos.value.find(el => el['id'] === logoId.value)?.url || null;
});

function setLogoFromPark(park) {
  let logos = [];
  for (const cs of park['chargingstations']) logos.push(cs['override_logo'] || null);
  // If every CS has the same logo, preload it
  logoId.value = logos.length > 0 && logos.every(e => e === logos[0]) ? logos[0] : null;
}

function setMDFromPark(park) {
  let mds = [];
  for (const cs of park['chargingstations']) mds.push(cs['money_receiver'] || null);
  // If every CS has the same MD, preload it
  moneyDestId.value = mds.length > 0 && mds.every(e => e === mds[0]) ? mds[0] : null;
}

async function submitMD() {
  const payload = {
    'money_receiver': moneyDestId.value
  };
  console.log(payload);
  let operations = [];
  loadingOverlay.value = true;
  useAlerts.value.suppressAlert(true);
  for (const cs of originalPark.value['chargingstations']) operations.push(modifyPatchCS(useAlerts, payload, originalPark.value.id, cs['park_bnum']));
  try {
    await Promise.all(operations);
    useAlerts.value.suppressAlert(false);
    useAlerts.value.successAlert("Saved Money Destination for all Charging Stations", "");
  } catch (e) {
    useAlerts.value.suppressAlert(false);
    useAlerts.value.errorAlert('Error during MoneyDest CS save', e.toString());
  } finally {
    loadingOverlay.value = false;
    emit('updated');
  }
}

async function submitLogo() {
  const payload = {
    'override_logo': logoId.value
  };
  console.log(payload);
  let operations = [];
  loadingOverlay.value = true;
  useAlerts.value.suppressAlert(true);
  for (const cs of originalPark.value['chargingstations']) operations.push(modifyPatchCS(useAlerts, payload, originalPark.value.id, cs['park_bnum']));
  try {
    await Promise.all(operations);
    useAlerts.value.suppressAlert(false);
  } catch (e) {
    useAlerts.value.suppressAlert(false);
    useAlerts.value.errorAlert('Error during Logo CS save', e.toString());
  } finally {
    loadingOverlay.value = false;
    emit('updated');
  }
}

watch(originalPark, setLogoFromPark);
watch(originalPark, setMDFromPark);

// But JUST TO BE SURE, let's make a onMounted too, that just get skipped if the prop is null
onMounted(() => {
  if (originalPark.value !== null) {
    setLogoFromPark(originalPark.value);
    setMDFromPark(originalPark.value);
  }
});
</script>

<i18n>
{
  "en": {
    "apply_to_all_cs": "Apply to all CS",
    "money_destination": "Money Destination",
    "apply_money_destination_to_all_cs": "Apply Money Destination to all CS",
    "charge_re_logo": "Charge.re Logo",
    "frontend_logo_image": "Frontend Logo Image",
    "preview": "Preview",
    "choose_logo_preview": "Choose a logo to see a preview",
    "apply_logo_to_all_cs": "Apply Logo to all CS"
  },
  "it": {
    "apply_to_all_cs": "Applica a tutte le CS",
    "money_destination": "Destinazione Denaro",
    "apply_money_destination_to_all_cs": "Applica Destinazione Denaro a tutte le CS",
    "charge_re_logo": "Logo Charge.re",
    "frontend_logo_image": "Immagine Logo Frontend",
    "preview": "Anteprima",
    "choose_logo_preview": "Scegli un logo per vedere l'anteprima",
    "apply_logo_to_all_cs": "Applica Logo a tutte le CS"
  },
  "fr": {
    "apply_to_all_cs": "Appliquer à toutes les CS",
    "money_destination": "Destination d'Argent",
    "apply_money_destination_to_all_cs": "Appliquer la Destination d'Argent à toutes les CS",
    "charge_re_logo": "Logo Charge.re",
    "frontend_logo_image": "Image Logo Frontend",
    "preview": "Aperçu",
    "choose_logo_preview": "Choisissez un logo pour voir l'aperçu",
    "apply_logo_to_all_cs": "Appliquer le Logo à toutes les CS"
  }
}
</i18n>

<style scoped>

</style>
